import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { useDispatch } from "react-redux";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import useToggle from "@base/hooks/useToggle";
import { activeItems, errorBodyHas, selectedItemIds } from "@base/modules/patientSpecialPackage/packageDetailRules";
import { ISpecialPackage, ISpecialServiceDetailItem } from "@base/modules/patientSpecialPackage/patientSpecialPackage.interface";
import patientSpecialPackageService from "@base/modules/patientSpecialPackage/patientSpecialPackageService";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";

/** SELECT: still open to choose · FIXED: confirmed, continue the booking · VIEW: read-only recap. */
export type ServiceItemsMode = "SELECT" | "FIXED" | "VIEW";

/** How the confirmation went - `SYNC_FAILED` keeps the patient's picks locally and says so afterwards. */
export type ServiceItemsOutcome = "SAVED" | "SYNC_FAILED" | "UNCHANGED";

interface IProps {
  open: boolean;
  toggle: () => void;
  mode: ServiceItemsMode;
  title?: string;
  packageKey?: string;
  items: ISpecialServiceDetailItem[];
  /** What the chosen items may add up to; 0 means no limit. */
  cap: number;
  exceedLimitMessage?: string;
  /** SELECT confirmed (with the updated package when the server sent one) or FIXED continued. */
  onDone?: (items: ISpecialServiceDetailItem[], outcome: ServiceItemsOutcome, updated?: ISpecialPackage) => void;
}

/**
 * The catalogue of a group checkup (§10.3). Mandatory items are always in; optional ones are ticked
 * up to the cap. Confirming is final, so it is asked once more and always sent to the server.
 */
export default function ServiceItemsPicker({ open, toggle, mode, title, packageKey, items, cap, exceedLimitMessage, onDone }: IProps) {
  //! state
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { open: openFinal, shouldRender: shouldRenderFinal, toggle: toggleFinal } = useToggle();
  const catalogue = useMemo(() => activeItems(items), [items]);
  const [chosenIds, setChosenIds] = useState<number[]>(() =>
    catalogue.filter((item) => item.selected || item.mandatory).map((item) => Number(item.id)),
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ mandatory: false, optional: true });

  //! function
  const exceedMessage = exceedLimitMessage || t("packageBooking.amountExceeded");

  /** Going over the cap is said where the patient is looking (the list) and as a notification. */
  const showOverCap = () => {
    setError(exceedMessage);
    dispatch(setAlertMessage({ type: "warning", message: t("Notification"), description: exceedMessage }));
  };
  const isEditable = mode === "SELECT";
  // While choosing, the ticks decide; a recap shows what was chosen (its items may have no id).
  const isChosen = (item: ISpecialServiceDetailItem) =>
    !!item.mandatory || (isEditable ? chosenIds.includes(Number(item.id)) : !!item.selected);
  const visible = isEditable ? catalogue : catalogue.filter((item) => item.selected || item.mandatory);
  const chosenItems = catalogue.filter(isChosen);

  const totalOf = (ids: number[]) =>
    catalogue.filter((item) => item.mandatory || ids.includes(Number(item.id))).reduce((sum, item) => sum + Number(item.amount ?? 0), 0);

  const toggleItem = (item: ISpecialServiceDetailItem) => {
    if (!isEditable || item.mandatory) {
      return;
    }

    const id = Number(item.id);

    if (chosenIds.includes(id)) {
      setError("");
      setChosenIds(chosenIds.filter((chosenId) => chosenId !== id));
      return;
    }

    if (cap > 0 && totalOf([...chosenIds, id]) > cap) {
      showOverCap();
      return;
    }

    setError("");
    setChosenIds([...chosenIds, id]);
  };

  const onPrimary = () => {
    if (mode === "VIEW") {
      toggle();
      return;
    }

    if (mode === "FIXED") {
      onDone?.(visible, "UNCHANGED");
      return;
    }

    toggleFinal();
  };

  const submit = async () => {
    const ids = selectedItemIds(catalogue, chosenIds);

    try {
      setIsSaving(true);
      setError("");
      const resp = await patientSpecialPackageService.selectServiceItems(String(packageKey), ids);
      const updated = resp.data && resp.data.key === packageKey && resp.data.patient_special_package_details?.length ? resp.data : undefined;

      toggleFinal();
      onDone?.(chosenItems, updated ? "SAVED" : "SYNC_FAILED", updated);
    } catch (saveError) {
      const axiosError = saveError as AxiosError;

      toggleFinal();

      if (!axiosError.response) {
        setError(t("booking.noConnection"));
        return;
      }

      if (errorBodyHas(axiosError.response.data, "AMOUNT_EXCEEDED")) {
        showOverCap();
        return;
      }

      // Already confirmed on the server, or any other refusal: the picks are kept and the clinic confirms them.
      onDone?.(chosenItems, "SYNC_FAILED");
    } finally {
      setIsSaving(false);
    }
  };

  //! render
  const renderItem = (item: ISpecialServiceDetailItem, index: number) => {
    const chosen = isChosen(item);

    return (
      <div
        key={item.id ?? item.code ?? index}
        onClick={() => toggleItem(item)}
        className={`p-3 rounded-lg border flex items-center gap-3 ${chosen ? "border-common-primary bg-common-primary50" : "border-common-divider bg-common-white"} ${
          isEditable && !item.mandatory ? "cursor-pointer" : "cursor-default"
        }`}
      >
        {isEditable ? <CommonStyles.CheckBoxUI checked={chosen} disabled={!!item.mandatory} /> : null}
        <span className="text-common-maintext text-sm font-medium font-['Inter']">{item.name}</span>
      </div>
    );
  };

  const mandatory = visible.filter((item) => item.mandatory);
  const optional = visible.filter((item) => !item.mandatory);
  const isGrouped = mandatory.length > 0 && optional.length > 0;

  const renderGroup = (key: "mandatory" | "optional", list: ISpecialServiceDetailItem[]) => (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }))}
        className={`flex justify-between items-center text-sm font-semibold font-['Inter'] uppercase ${
          key === "mandatory" ? "text-common-subtext" : "text-common-primary"
        }`}
      >
        <span>{t(`booking.groupExam.${key}Title`, { count: list.filter(isChosen).length })}</span>
        {openGroups[key] ? <CommonIcons.UpOutlined /> : <CommonIcons.DownOutlined />}
      </button>
      {openGroups[key] ? list.map(renderItem) : null}
    </div>
  );

  const primaryLabel = mode === "SELECT" ? t("booking.groupExam.confirm") : mode === "FIXED" ? t("packageBooking.continue") : t("Close");

  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      width={640}
      title={title || t("booking.groupExam.title")}
      hiddenAction
      footer={
        <div className="flex justify-between items-center gap-3">
          {/* The error sits under the count, in the footer, so a long list never scrolls it out of sight. */}
          <div className="flex flex-col items-start gap-1 text-left">
            <span className="text-common-subtext text-sm font-['Inter']">
              {t("booking.groupExam.selectedCount")} <b className="text-common-maintext">{chosenItems.length}</b> {t("booking.groupExam.itemsUnit")}
            </span>
            {error ? <span className="text-common-red500 text-sm font-normal font-['Inter']">{error}</span> : null}
          </div>
          <CommonStyles.ButtonUI className="h-12 px-5" loading={isSaving} onClick={onPrimary}>
            {isSaving ? t("booking.groupExam.saving") : primaryLabel}
          </CommonStyles.ButtonUI>
        </div>
      }
      content={
        <div className="flex flex-col gap-3 max-h-[calc(100vh-320px)] overflow-y-auto">
          {!visible.length ? (
            <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-normal font-['Inter']">
              {catalogue.length ? t("booking.groupExam.noneSelected") : t("booking.groupExam.empty")}
            </div>
          ) : isGrouped ? (
            <>
              {renderGroup("mandatory", mandatory)}
              {renderGroup("optional", optional)}
            </>
          ) : (
            visible.map(renderItem)
          )}

          {shouldRenderFinal && (
            <CommonStyles.ModalConfirmationUI
              open={openFinal}
              toggle={toggleFinal}
              title={t("Notification")}
              content={t("packageBooking.confirmServiceSelection")}
              okText={t("booking.agree")}
              okButtonProps={{ loading: isSaving }}
              onConfirm={submit}
            />
          )}
        </div>
      }
    />
  );
}
